import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, USER_TIERS } from '../../contexts/AuthContext';
import { useCommunity, REPORT_STATUS } from '../../contexts/CommunityContext';
import { useNotification, NOTIFICATION_TYPES } from '../../contexts/NotificationContext';
import { getToken } from '../../shared/auth';
import ServerMonitor from './ServerMonitor';
import ApiDashboard from './ApiDashboard';
import ProjectManagement from './ProjectManagement';
import TaskSummaryWidget from './TaskSummaryWidget';

// Admin API 헬퍼 함수
const adminFetch = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers
  };

  return fetch(`https://api.ilouli.com${endpoint}`, {
    ...options,
    headers,
    credentials: 'include'
  });
};

const Admin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getAllUsers, updateUserTier, deleteUser, getPendingUsers, approveUser, rejectUser, user } = useAuth();
  const { getReports, handleReport, getHiddenPosts, restorePost } = useCommunity();
  const { addNotification } = useNotification();

  // Sidebar 상태
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('admin_sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // 메뉴 상태
  const [activeMenu, setActiveMenu] = useState(() => {
    return localStorage.getItem('admin_activeMenu') || 'users';
  });
  const [activeSubMenu, setActiveSubMenu] = useState(() => {
    return localStorage.getItem('admin_activeSubMenu') || 'all-users';
  });

  // 데이터 상태
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [hiddenPosts, setHiddenPosts] = useState([]);
  const [message, setMessage] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [apiFilter, setApiFilter] = useState('all');
  const [apiKeyStatus, setApiKeyStatus] = useState({
    openai: false,
    kling: false,
    replicate: false
  });

  // API 키 모달 상태
  const [keyModal, setKeyModal] = useState({ open: false, keyName: '', keyLabel: '', keyDesc: '' });
  const [modalKeyValue, setModalKeyValue] = useState('');
  const [savingKeys, setSavingKeys] = useState(false);
  const [keyMessage, setKeyMessage] = useState('');

  // 툴팁 상태
  const [activeTooltip, setActiveTooltip] = useState(null);

  // API 키 정보 매핑
  const apiKeyInfo = {
    openai: {
      label: 'OpenAI',
      desc: 'GPT-4, TTS, 숏폼 영상 생성에 사용',
      placeholder: 'sk-...',
      guide: {
        url: 'https://platform.openai.com/api-keys',
        steps: [
          '1. OpenAI 계정 로그인 (없으면 가입)',
          '2. API Keys 페이지에서 "Create new secret key" 클릭',
          '3. 생성된 키 복사 (sk-로 시작)'
        ],
        pricing: '사용량 기반 과금 (GPT-4: $0.03/1K tokens)'
      }
    },
    kling: {
      label: 'Kling AI',
      desc: '이미지→영상 변환에 사용',
      placeholder: 'API 키 입력...',
      guide: {
        url: 'https://klingai.com',
        steps: [
          '1. Kling AI 웹사이트 가입',
          '2. 대시보드 → API 섹션 이동',
          '3. API 키 생성 및 복사'
        ],
        pricing: '크레딧 기반 과금 (영상 생성당 크레딧 차감)'
      }
    },
    replicate: {
      label: 'Replicate',
      desc: '이미지 업스케일링 (Real-ESRGAN)에 사용',
      placeholder: 'r8_...',
      guide: {
        url: 'https://replicate.com/account/api-tokens',
        steps: [
          '1. Replicate 계정 로그인 (GitHub 연동 가능)',
          '2. Account → API tokens 이동',
          '3. "Create token" 클릭 후 복사 (r8_로 시작)'
        ],
        pricing: '사용량 기반 과금 (업스케일당 약 $0.01~0.05)'
      }
    }
  };

  const apiKeyMapping = {
    11: 'kling',
    12: 'replicate',
    13: 'openai'
  };

  // API 목록
  const apiList = [
    { id: 1, category: '인증', method: 'POST', endpoint: '/api/auth/login', description: '로그인', status: 'completed', priority: 'high' },
    { id: 2, category: '인증', method: 'POST', endpoint: '/api/auth/signup', description: '회원가입', status: 'completed', priority: 'high' },
    { id: 3, category: '인증', method: 'POST', endpoint: '/api/auth/social-login', description: '소셜 로그인', status: 'completed', priority: 'high' },
    { id: 4, category: '인증', method: 'GET', endpoint: '/api/auth/me', description: '현재 사용자 정보', status: 'completed', priority: 'high' },
    { id: 5, category: '사용자', method: 'GET', endpoint: '/api/users', description: '전체 회원 목록', status: 'completed', priority: 'high' },
    { id: 6, category: '사용자', method: 'GET', endpoint: '/api/users/pending', description: '승인 대기 회원', status: 'completed', priority: 'high' },
    { id: 7, category: '사용자', method: 'POST', endpoint: '/api/users/:id/approve', description: '회원 승인', status: 'completed', priority: 'high' },
    { id: 8, category: '사용자', method: 'POST', endpoint: '/api/users/:id/reject', description: '회원 거절', status: 'completed', priority: 'high' },
    { id: 9, category: '사용자', method: 'PUT', endpoint: '/api/users/:id/tier', description: '등급 변경', status: 'completed', priority: 'high' },
    { id: 10, category: '사용자', method: 'DELETE', endpoint: '/api/users/:id', description: '회원 삭제', status: 'completed', priority: 'high' },
    { id: 11, category: 'AI', method: 'POST', endpoint: '/api/ai/image-to-video', description: '이미지→영상', status: 'needs-key', priority: 'high', note: 'Kling API 키 필요' },
    { id: 12, category: 'AI', method: 'POST', endpoint: '/api/ai/upscale', description: '이미지 업스케일', status: 'needs-key', priority: 'high', note: 'Replicate API 키 필요' },
    { id: 13, category: 'AI', method: 'POST', endpoint: '/api/ai/shortform/generate', description: '숏폼 영상 생성', status: 'needs-key', priority: 'high', note: 'OpenAI API 키 필요' },
    { id: 14, category: 'AI', method: 'GET', endpoint: '/api/ai/job/:jobId', description: 'AI 작업 상태', status: 'completed', priority: 'medium' },
    { id: 15, category: 'AI', method: 'GET', endpoint: '/api/ai/job/:jobId/download', description: '결과물 다운로드', status: 'completed', priority: 'medium' },
    { id: 16, category: 'AI', method: 'GET', endpoint: '/api/ai/usage', description: 'AI 사용량 통계', status: 'completed', priority: 'low' },
    { id: 17, category: '커뮤니티', method: 'GET', endpoint: '/api/community/posts', description: '게시글 목록', status: 'completed', priority: 'medium' },
    { id: 18, category: '커뮤니티', method: 'POST', endpoint: '/api/community/posts', description: '게시글 작성', status: 'completed', priority: 'medium' },
    { id: 19, category: '커뮤니티', method: 'GET', endpoint: '/api/community/posts/:id', description: '게시글 상세', status: 'completed', priority: 'medium' },
    { id: 20, category: '커뮤니티', method: 'PUT', endpoint: '/api/community/posts/:id', description: '게시글 수정', status: 'completed', priority: 'medium' },
    { id: 21, category: '커뮤니티', method: 'DELETE', endpoint: '/api/community/posts/:id', description: '게시글 삭제', status: 'completed', priority: 'medium' },
    { id: 22, category: '커뮤니티', method: 'POST', endpoint: '/api/community/posts/:id/comments', description: '댓글 작성', status: 'completed', priority: 'medium' },
    { id: 23, category: '커뮤니티', method: 'POST', endpoint: '/api/community/posts/:id/report', description: '신고', status: 'completed', priority: 'low' },
    { id: 24, category: '파일', method: 'POST', endpoint: '/api/files/upload', description: '파일 업로드', status: 'completed', priority: 'high' },
    { id: 25, category: '파일', method: 'GET', endpoint: '/api/files/view/:filename', description: '파일 조회', status: 'completed', priority: 'high' },
    { id: 26, category: '파일', method: 'DELETE', endpoint: '/api/files/:id', description: '파일 삭제', status: 'completed', priority: 'low' },
    { id: 27, category: '파일', method: 'GET', endpoint: '/api/files/list', description: '파일 목록', status: 'completed', priority: 'low' },
    { id: 28, category: '알림', method: 'GET', endpoint: '/api/notifications', description: '알림 목록', status: 'completed', priority: 'medium' },
    { id: 29, category: '알림', method: 'PUT', endpoint: '/api/notifications/:id/read', description: '알림 읽음', status: 'completed', priority: 'medium' },
    { id: 30, category: '알림', method: 'DELETE', endpoint: '/api/notifications/:id', description: '알림 삭제', status: 'completed', priority: 'low' },
  ];

  // 메뉴 구조
  const menuStructure = [
    {
      id: 'users',
      label: '회원 관리',
      icon: '👥',
      submenus: [
        { id: 'all-users', label: '전체 회원' },
        { id: 'pending-users', label: '승인 대기', badge: pendingUsers.length }
      ]
    },
    {
      id: 'moderation',
      label: '콘텐츠 관리',
      icon: '🛡️',
      submenus: [
        { id: 'reports', label: '신고 관리', badge: reports.filter(r => r.status === REPORT_STATUS.PENDING).length },
        { id: 'hidden-posts', label: '숨김 게시글', badge: hiddenPosts.length }
      ]
    },
    {
      id: 'projects',
      label: '프로젝트',
      icon: '📁',
      submenus: [
        { id: 'project-board', label: '프로젝트 게시판' },
        { id: 'project-tracking', label: '진행 상황 추적' }
      ]
    },
    {
      id: 'api',
      label: 'API 관리',
      icon: '🔌',
      submenus: [
        { id: 'api-dashboard', label: 'API 대시보드' }
      ]
    },
    {
      id: 'server',
      label: '서버 관리',
      icon: '🖥️',
      submenus: [
        { id: 'server-monitor', label: '시스템 모니터링' },
        { id: 'pm2-processes', label: 'PM2 프로세스' },
        { id: 'database', label: '데이터베이스' },
        { id: 'logs', label: '로그' }
      ]
    },
  ];

  // 데이터 로드 함수들
  const loadUsers = async () => {
    try {
      const allUsers = await getAllUsers();
      setUsers(allUsers.filter(u => u.status === 'approved' || !u.status));
    } catch (err) {
      setMessage(t('admin.error.loadFailed'));
    }
  };

  const loadPendingUsers = async () => {
    try {
      const pending = await getPendingUsers();
      setPendingUsers(pending);
    } catch (err) {
      console.error('Failed to load pending users');
    }
  };

  const loadReports = async () => {
    try {
      const allReports = await getReports();
      setReports(allReports || []);
    } catch (err) {
      console.error('Failed to load reports');
    }
  };

  const loadHiddenPosts = () => {
    try {
      const hidden = getHiddenPosts();
      setHiddenPosts(hidden);
    } catch (err) {
      console.error('Failed to load hidden posts');
    }
  };

  const loadApiKeyStatus = async () => {
    try {
      const response = await adminFetch('/api/admin/api-keys/status');
      if (response.ok) {
        const data = await response.json();
        setApiKeyStatus(data.status || {});
      }
    } catch (err) {
      console.error('Failed to load API key status');
    }
  };

  // API 키 저장
  const saveApiKey = async (keyName, keyValue) => {
    if (!keyValue.trim()) {
      setKeyMessage('API 키를 입력해주세요.');
      return;
    }
    setSavingKeys(true);
    try {
      const response = await adminFetch('/api/admin/api-keys', {
        method: 'POST',
        body: JSON.stringify({ keyName, keyValue })
      });
      if (response.ok) {
        setKeyMessage(`${keyName.toUpperCase()} API 키가 저장되었습니다.`);
        loadApiKeyStatus();
      } else {
        const data = await response.json();
        setKeyMessage(data.error || 'API 키 저장 실패');
      }
    } catch (err) {
      setKeyMessage('API 키 저장 중 오류 발생');
    }
    setSavingKeys(false);
    setTimeout(() => setKeyMessage(''), 3000);
  };

  const deleteApiKey = async (keyName) => {
    if (!window.confirm(`${apiKeyInfo[keyName]?.label || keyName} API 키를 삭제하시겠습니까?`)) return;
    try {
      const response = await adminFetch(`/api/admin/api-keys/${keyName}`, { method: 'DELETE' });
      if (response.ok) {
        setKeyMessage(`${apiKeyInfo[keyName]?.label || keyName} API 키가 삭제되었습니다.`);
        loadApiKeyStatus();
        setKeyModal({ open: false, keyName: '', keyLabel: '', keyDesc: '' });
      }
    } catch (err) {
      setKeyMessage('API 키 삭제 중 오류 발생');
    }
    setTimeout(() => setKeyMessage(''), 3000);
  };

  const openKeyModal = (keyName) => {
    const info = apiKeyInfo[keyName];
    if (!info) return;
    setKeyModal({ open: true, keyName, keyLabel: info.label, keyDesc: info.desc });
    setModalKeyValue('');
  };

  const saveKeyFromModal = async () => {
    if (!modalKeyValue.trim()) {
      setKeyMessage('API 키를 입력해주세요.');
      setTimeout(() => setKeyMessage(''), 3000);
      return;
    }
    await saveApiKey(keyModal.keyName, modalKeyValue);
    setKeyModal({ open: false, keyName: '', keyLabel: '', keyDesc: '' });
    setModalKeyValue('');
  };

  const handleStatusClick = (api) => {
    const keyName = apiKeyMapping[api.id];
    if (keyName) {
      openKeyModal(keyName);
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    if (user && user.tier === 'admin') {
      loadUsers();
      loadPendingUsers();
      loadReports();
      loadHiddenPosts();
      loadApiKeyStatus();
    }
  }, [user]);

  // localStorage 저장
  useEffect(() => {
    localStorage.setItem('admin_activeMenu', activeMenu);
  }, [activeMenu]);

  useEffect(() => {
    localStorage.setItem('admin_activeSubMenu', activeSubMenu);
  }, [activeSubMenu]);

  useEffect(() => {
    localStorage.setItem('admin_sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // 사용자 관리 함수들
  const handleTierChange = async (userId, newTier) => {
    try {
      await updateUserTier(userId, newTier);
      setMessage(t('admin.success.tierUpdated'));
      await loadUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(t('admin.error.updateFailed'));
    }
  };

  const handleDelete = async (userId, userName) => {
    if (window.confirm(t('admin.confirm.delete', { name: userName }))) {
      try {
        await deleteUser(userId);
        setMessage(t('admin.success.deleted'));
        await loadUsers();
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        setMessage(err.message === 'Cannot delete yourself'
          ? t('admin.error.cannotDeleteSelf')
          : t('admin.error.deleteFailed'));
      }
    }
  };

  const handleApprove = async (userId, userName) => {
    try {
      await approveUser(userId);
      setMessage(t('admin.success.approved', { name: userName }));
      await loadUsers();
      await loadPendingUsers();
      setTimeout(() => setMessage(''), 3000);
      addNotification(userId, NOTIFICATION_TYPES.APPROVAL, t('notification.messages.accountApproved'), t('notification.messages.accountApproved'), '/profile');
    } catch (err) {
      setMessage(t('admin.error.approveFailed'));
    }
  };

  const handleReject = async (userId, userName) => {
    if (window.confirm(t('admin.confirm.reject', { name: userName }))) {
      try {
        addNotification(userId, NOTIFICATION_TYPES.APPROVAL, t('notification.messages.accountRejected'), t('notification.messages.accountRejected'), null);
        await rejectUser(userId);
        setMessage(t('admin.success.rejected', { name: userName }));
        await loadPendingUsers();
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        setMessage(t('admin.error.rejectFailed'));
      }
    }
  };

  const handleReportAction = (reportId, action) => {
    try {
      const report = reports.find(r => r.id === reportId);
      handleReport(reportId, action, true);
      setMessage(t('admin.moderation.success.' + action));
      loadReports();
      loadHiddenPosts();
      setTimeout(() => setMessage(''), 3000);
      if (report && report.reporter) {
        addNotification(
          report.reporter.id,
          NOTIFICATION_TYPES.REPORT_RESULT,
          t('notification.messages.reportProcessed'),
          action === 'hide' ? t('admin.moderation.success.hide') : t('admin.moderation.success.dismiss'),
          '/community'
        );
      }
    } catch (err) {
      setMessage(t('admin.moderation.error.actionFailed'));
    }
  };

  const handleRestorePost = (postId) => {
    try {
      restorePost(postId, true);
      setMessage(t('admin.moderation.success.restored'));
      loadHiddenPosts();
      loadReports();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(t('admin.moderation.error.restoreFailed'));
    }
  };

  // 유틸리티 함수들
  const getTierDisplayName = (tier) => {
    const tierMap = {
      guest: t('auth.tiers.guest'),
      general: t('auth.tiers.general'),
      subscriber: t('auth.tiers.subscriber'),
      family: t('auth.tiers.family'),
      admin: t('auth.tiers.admin')
    };
    return tierMap[tier] || tier;
  };

  const getProviderInfo = (socialProvider) => {
    if (socialProvider === 'google') return { name: 'Google', icon: '🔵', className: 'bg-blue-50 text-blue-700' };
    if (socialProvider === 'kakao') return { name: 'Kakao', icon: '🟡', className: 'bg-yellow-100 text-yellow-800' };
    return { name: '이메일', icon: '✉️', className: 'bg-gray-100 text-gray-600' };
  };

  const filteredUsers = users.filter(u => {
    let passesProviderFilter = true;
    if (providerFilter !== 'all') {
      if (providerFilter === 'google') passesProviderFilter = u.social_provider === 'google';
      else if (providerFilter === 'kakao') passesProviderFilter = u.social_provider === 'kakao';
      else if (providerFilter === 'email') passesProviderFilter = !u.social_provider;
    }
    let passesTierFilter = true;
    if (tierFilter !== 'all') passesTierFilter = u.tier === tierFilter;
    return passesProviderFilter && passesTierFilter;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case REPORT_STATUS.PENDING: return 'bg-orange-100 text-orange-600';
      case REPORT_STATUS.REVIEWED: return 'bg-blue-100 text-blue-600';
      case REPORT_STATUS.DISMISSED: return 'bg-gray-100 text-gray-600';
      case REPORT_STATUS.ACTIONED: return 'bg-green-100 text-green-600';
      default: return '';
    }
  };

  const pendingReports = reports.filter(r => r.status === REPORT_STATUS.PENDING);

  // 메뉴 클릭 핸들러
  const handleMenuClick = (menuId, subMenuId) => {
    setActiveMenu(menuId);
    setActiveSubMenu(subMenuId);
  };

  // 현재 페이지 제목
  const getCurrentPageTitle = () => {
    const menuItem = menuStructure.find(m => m.id === activeMenu);
    if (menuItem) {
      const subMenuItem = menuItem.submenus.find(s => s.id === activeSubMenu);
      return subMenuItem?.label || menuItem.label;
    }
    return t('admin.title');
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sidebar */}
      <aside className={`fixed top-[60px] left-0 h-[calc(100vh-60px)] bg-[rgba(251,251,253,0.8)] backdrop-blur-xl backdrop-saturate-[180%] border-r border-black/5 flex flex-col transition-[width] duration-200 z-[100] ${sidebarCollapsed ? 'w-16' : 'w-[260px]'}`}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/5 min-h-[44px]">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#1d1d1f] tracking-tight">Admin</span>
              <a href="https://ilouli.com" className="text-xs text-[#767676] no-underline hover:text-[#666666] transition-colors" target="_blank" rel="noopener noreferrer">
                ilouli.com
              </a>
            </div>
          )}
          {sidebarCollapsed && <span className="text-lg font-bold text-[#1d1d1f]">A</span>}
          <button
            className="w-8 h-8 flex items-center justify-center bg-transparent border-none rounded-lg cursor-pointer hover:bg-[#fafafa] transition-colors"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label={sidebarCollapsed ? '사이드바 펼치기' : '사이드바 접기'}
          >
            <svg className={`w-4 h-4 text-[#666666] transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>

        {/* Sidebar Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 scrollbar-thin scrollbar-thumb-[#e2e8f0] scrollbar-track-transparent">
          {menuStructure.map((menu) => (
            <div key={menu.id} className="mb-1">
              {!sidebarCollapsed && (
                <div className="px-4 pt-4 pb-1.5 text-[11px] font-semibold text-[#86868b] uppercase tracking-wide">{menu.icon} {menu.label}</div>
              )}
              {sidebarCollapsed && (
                <div className="py-3 px-4 text-lg text-center cursor-default" title={menu.label}>{menu.icon}</div>
              )}
              <div className="flex flex-col gap-0.5 px-2">
                {menu.submenus.map((submenu) => (
                  <button
                    key={submenu.id}
                    className={`flex items-center justify-between w-full py-2 px-3 bg-transparent border-none rounded-md cursor-pointer transition-all duration-150 text-left hover:opacity-65 ${activeSubMenu === submenu.id ? 'bg-black/[0.04]' : ''}`}
                    onClick={() => handleMenuClick(menu.id, submenu.id)}
                    title={sidebarCollapsed ? submenu.label : undefined}
                  >
                    {!sidebarCollapsed && (
                      <>
                        <span className={`text-xs text-[#1d1d1f] ${activeSubMenu === submenu.id ? 'font-medium' : 'font-normal'}`}>{submenu.label}</span>
                        {submenu.badge > 0 && <span className="bg-red-500 text-white text-[11px] font-semibold px-2 py-0.5 rounded-[10px] min-w-[20px] text-center">{submenu.badge}</span>}
                      </>
                    )}
                    {sidebarCollapsed && submenu.badge > 0 && (
                      <span className="w-2 h-2 bg-red-500 rounded-full absolute top-2 right-2"></span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 min-h-[calc(100vh-60px)] mt-[60px] transition-[margin-left] duration-200 ${sidebarCollapsed ? 'ml-16' : 'ml-[260px]'}`}>
        {/* Content Header */}
        <header className="sticky top-[60px] bg-white border-b border-[#e2e8f0] py-5 px-8 z-50">
          <h1 className="text-2xl font-bold text-[#1d1d1f] m-0 tracking-tight">{getCurrentPageTitle()}</h1>
        </header>

        {/* Messages */}
        {message && (
          <div className={`mx-8 mt-4 px-4 py-3 rounded-lg text-sm font-medium ${message.includes('error') || message.includes('실패') ? 'bg-red-100 text-red-500 border border-red-300' : 'bg-green-100 text-green-600 border border-green-300'}`}>
            {message}
          </div>
        )}

        {/* Content Area */}
        <div className="p-6 px-8 max-w-[1400px]">
          {/* 전체 회원 */}
          {activeSubMenu === 'all-users' && (
            <div className="animate-fade-in">
              <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4 mb-6">
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 text-center transition-all hover:border-[#cbd5e1] hover:shadow-md">
                  <span className="block text-[32px] font-bold text-[#1d1d1f] leading-tight mb-1">{users.length}</span>
                  <span className="text-[13px] text-[#666666] font-medium">{t('admin.stats.totalUsers')}</span>
                </div>
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 text-center transition-all hover:border-[#cbd5e1] hover:shadow-md">
                  <span className="block text-[32px] font-bold text-[#1d1d1f] leading-tight mb-1">{pendingUsers.length}</span>
                  <span className="text-[13px] text-[#666666] font-medium">{t('admin.stats.pendingUsers')}</span>
                </div>
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 text-center transition-all hover:border-[#cbd5e1] hover:shadow-md">
                  <span className="block text-[32px] font-bold text-[#1d1d1f] leading-tight mb-1">{users.filter(u => u.tier === 'family').length}</span>
                  <span className="text-[13px] text-[#666666] font-medium">{t('admin.stats.familyMembers')}</span>
                </div>
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 text-center transition-all hover:border-[#cbd5e1] hover:shadow-md">
                  <span className="block text-[32px] font-bold text-[#1d1d1f] leading-tight mb-1">{users.filter(u => u.tier === 'admin').length}</span>
                  <span className="text-[13px] text-[#666666] font-medium">{t('admin.stats.admins')}</span>
                </div>
              </div>

              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-[#1d1d1f] m-0">{t('admin.members.title')}</h2>
                <button onClick={() => navigate('/signup')} className="px-5 py-2.5 bg-blue-500 text-white border-none rounded-lg text-sm font-semibold cursor-pointer hover:bg-blue-600 transition-colors">+ 새 계정 생성</button>
              </div>

              <div className="bg-[#f5f5f7] border border-[#e2e8f0] rounded-xl px-5 py-4 mb-5">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-semibold text-[#767676] uppercase tracking-wide min-w-[70px]">가입방법:</span>
                  <div className="flex gap-2 flex-wrap">
                    {['all', 'google', 'kakao', 'email'].map(f => (
                      <button
                        key={f}
                        className={`px-3.5 py-1.5 border rounded-full text-[13px] font-medium cursor-pointer transition-all ${providerFilter === f ? 'bg-[#1d1d1f] text-white border-[#1d1d1f]' : 'bg-white border-[#e2e8f0] text-[#666666] hover:border-[#cbd5e1] hover:bg-[#fafafa]'}`}
                        onClick={() => setProviderFilter(f)}
                      >
                        {f === 'all' ? `전체 (${users.length})` :
                         f === 'google' ? `🔵 Google (${users.filter(u => u.social_provider === 'google').length})` :
                         f === 'kakao' ? `🟡 Kakao (${users.filter(u => u.social_provider === 'kakao').length})` :
                         `✉️ 이메일 (${users.filter(u => !u.social_provider).length})`}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap mt-3 pt-3 border-t border-[#e2e8f0]">
                  <span className="text-xs font-semibold text-[#767676] uppercase tracking-wide min-w-[70px]">회원등급:</span>
                  <div className="flex gap-2 flex-wrap">
                    {['all', 'guest', 'general', 'subscriber', 'family', 'admin'].map(f => (
                      <button
                        key={f}
                        className={`px-3.5 py-1.5 border rounded-full text-[13px] font-medium cursor-pointer transition-all ${tierFilter === f ? 'bg-[#1d1d1f] text-white border-[#1d1d1f]' : 'bg-white border-[#e2e8f0] text-[#666666] hover:border-[#cbd5e1] hover:bg-[#fafafa]'}`}
                        onClick={() => setTierFilter(f)}
                      >
                        {f === 'all' ? `전체 (${users.length})` : `${getTierDisplayName(f)} (${users.filter(u => u.tier === f).length})`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-x-auto">
                <table className="w-full min-w-[800px] border-collapse">
                  <thead>
                    <tr>
                      <th className="p-3 px-4 text-left border-b border-[#e2e8f0] bg-[#f5f5f7] text-xs font-semibold text-[#666666] uppercase tracking-wide whitespace-nowrap">가입방법</th>
                      <th className="p-3 px-4 text-left border-b border-[#e2e8f0] bg-[#f5f5f7] text-xs font-semibold text-[#666666] uppercase tracking-wide whitespace-nowrap">{t('admin.table.name')}</th>
                      <th className="p-3 px-4 text-left border-b border-[#e2e8f0] bg-[#f5f5f7] text-xs font-semibold text-[#666666] uppercase tracking-wide whitespace-normal break-all max-w-[200px]">{t('admin.table.email')}</th>
                      <th className="p-3 px-4 text-left border-b border-[#e2e8f0] bg-[#f5f5f7] text-xs font-semibold text-[#666666] uppercase tracking-wide whitespace-nowrap">{t('admin.table.tier')}</th>
                      <th className="p-3 px-4 text-left border-b border-[#e2e8f0] bg-[#f5f5f7] text-xs font-semibold text-[#666666] uppercase tracking-wide whitespace-nowrap">가입일</th>
                      <th className="p-3 px-4 text-left border-b border-[#e2e8f0] bg-[#f5f5f7] text-xs font-semibold text-[#666666] uppercase tracking-wide whitespace-nowrap">마지막 접속</th>
                      <th className="p-3 px-4 text-left border-b border-[#e2e8f0] bg-[#f5f5f7] text-xs font-semibold text-[#666666] uppercase tracking-wide whitespace-nowrap">{t('admin.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => {
                      const providerInfo = getProviderInfo(u.social_provider);
                      return (
                        <tr key={u.id} className={`transition-colors hover:bg-[#f5f5f7] ${u.id === user.id ? 'bg-blue-50' : ''}`}>
                          <td className="p-3 px-4 border-b border-[#e2e8f0] text-sm text-[#1d1d1f] whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold ${providerInfo.className}`}>
                              {providerInfo.icon} {providerInfo.name}
                            </span>
                          </td>
                          <td className="p-3 px-4 border-b border-[#e2e8f0] text-sm text-[#1d1d1f] whitespace-nowrap">
                            {u.name}
                            {u.id === user.id && <span className="inline-block ml-1.5 px-1.5 py-0.5 bg-[#1d1d1f] text-white text-[10px] font-semibold rounded">{t('admin.you')}</span>}
                          </td>
                          <td className="p-3 px-4 border-b border-[#e2e8f0] text-sm text-[#1d1d1f] whitespace-normal break-all max-w-[200px]">{u.email}</td>
                          <td className="p-3 px-4 border-b border-[#e2e8f0] text-sm text-[#1d1d1f] whitespace-nowrap">
                            <select
                              value={u.tier}
                              onChange={(e) => handleTierChange(u.id, e.target.value)}
                              className="px-2.5 py-1.5 bg-white border border-[#e2e8f0] rounded-md text-[13px] cursor-pointer focus:outline-none focus:border-blue-500 transition-colors"
                            >
                              {['guest', 'general', 'subscriber', 'family', 'admin'].map(tier => (
                                <option key={tier} value={tier}>{getTierDisplayName(tier)}</option>
                              ))}
                            </select>
                          </td>
                          <td className="p-3 px-4 border-b border-[#e2e8f0] text-sm text-[#1d1d1f] whitespace-nowrap">{u.join_date || '-'}</td>
                          <td className="p-3 px-4 border-b border-[#e2e8f0] text-sm text-[#1d1d1f] whitespace-nowrap">{u.last_login ? formatDate(u.last_login) : '-'}</td>
                          <td className="p-3 px-4 border-b border-[#e2e8f0] text-sm text-[#1d1d1f] whitespace-nowrap">
                            <button
                              onClick={() => handleDelete(u.id, u.name)}
                              className="px-3 py-1.5 bg-red-100 text-red-500 border-none rounded-md text-xs font-semibold cursor-pointer hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={u.id === user.id}
                            >
                              {t('admin.table.delete')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 승인 대기 */}
          {activeSubMenu === 'pending-users' && (
            <div className="animate-fade-in">
              <h2 className="text-lg font-bold text-[#1d1d1f] mb-4">{t('admin.pending.title')}</h2>
              {pendingUsers.length === 0 ? (
                <p className="text-center py-12 px-6 bg-[#f5f5f7] border border-[#e2e8f0] rounded-xl text-[#767676] text-[15px]">승인 대기 중인 회원이 없습니다.</p>
              ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(360px,1fr))] gap-4">
                  {pendingUsers.map((u) => (
                    <div key={u.id} className="bg-orange-50 border border-orange-300 border-l-4 border-l-orange-500 rounded-xl p-5 flex justify-between items-center gap-4">
                      <div className="flex flex-col gap-1">
                        <strong className="text-base text-[#1d1d1f]">{u.name}</strong>
                        <span className="text-sm text-[#666666]">{u.email}</span>
                        <span className="text-[13px] text-[#767676]">{t('admin.pending.appliedOn')} {u.joinDate}</span>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button onClick={() => handleApprove(u.id, u.name)} className="px-5 py-2.5 bg-green-500 text-white border-none rounded-lg text-sm font-semibold cursor-pointer hover:bg-green-600 transition-colors">{t('admin.pending.approve')}</button>
                        <button onClick={() => handleReject(u.id, u.name)} className="px-5 py-2.5 bg-transparent text-red-500 border border-red-500 rounded-lg text-sm font-semibold cursor-pointer hover:bg-red-50 transition-colors">{t('admin.pending.reject')}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 신고 관리 */}
          {activeSubMenu === 'reports' && (
            <div className="animate-fade-in">
              <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4 mb-6">
                <div className="bg-white border border-[#e2e8f0] border-l-4 border-l-orange-500 rounded-xl p-5 text-center transition-all hover:border-[#cbd5e1] hover:shadow-md">
                  <span className="block text-[32px] font-bold text-[#1d1d1f] leading-tight mb-1">{pendingReports.length}</span>
                  <span className="text-[13px] text-[#666666] font-medium">{t('admin.moderation.stats.pending')}</span>
                </div>
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 text-center transition-all hover:border-[#cbd5e1] hover:shadow-md">
                  <span className="block text-[32px] font-bold text-[#1d1d1f] leading-tight mb-1">{reports.length}</span>
                  <span className="text-[13px] text-[#666666] font-medium">{t('admin.moderation.stats.total')}</span>
                </div>
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 text-center transition-all hover:border-[#cbd5e1] hover:shadow-md">
                  <span className="block text-[32px] font-bold text-[#1d1d1f] leading-tight mb-1">{hiddenPosts.length}</span>
                  <span className="text-[13px] text-[#666666] font-medium">{t('admin.moderation.stats.hidden')}</span>
                </div>
              </div>

              <h2 className="text-lg font-bold text-[#1d1d1f] mb-4">{t('admin.moderation.reports')}</h2>
              {reports.length === 0 ? (
                <p className="text-center py-12 px-6 bg-[#f5f5f7] border border-[#e2e8f0] rounded-xl text-[#767676] text-[15px]">{t('admin.moderation.noReports')}</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {reports.map((report) => (
                    <div key={report.id} className={`bg-white border border-[#e2e8f0] rounded-xl p-5 ${report.status === 'pending' ? 'border-l-4 border-l-orange-500' : report.status === 'actioned' ? 'border-l-4 border-l-green-500 opacity-80' : 'border-l-4 border-l-gray-400 opacity-80'}`}>
                      <div className="flex justify-between items-center mb-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded ${report.type === 'post' ? 'bg-blue-100 text-blue-500' : 'bg-gray-100 text-gray-600'}`}>
                          {report.type === 'post' ? t('admin.moderation.type.post') : t('admin.moderation.type.comment')}
                        </span>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded ${getStatusBadgeClass(report.status)}`}>
                          {t(`admin.moderation.status.${report.status}`)}
                        </span>
                      </div>
                      <div className="mb-4">
                        <p className="m-0 mb-2 text-[15px] text-[#1d1d1f]"><strong>{report.type === 'post' ? report.postTitle : report.commentContent}...</strong></p>
                        <p className="m-0 text-sm text-[#666666]">{t('admin.moderation.reason')}: {report.reason}</p>
                        <div className="flex flex-wrap gap-4 mt-3 text-[13px] text-[#767676]">
                          <span>{t('admin.moderation.reportedBy')}: {report.reporter.name}</span>
                          <span>{t('admin.moderation.author')}: {report.author.name}</span>
                          <span>{formatDate(report.createdAt)}</span>
                        </div>
                      </div>
                      {report.status === REPORT_STATUS.PENDING && (
                        <div className="flex gap-2 pt-4 border-t border-[#e2e8f0]">
                          <button onClick={() => handleReportAction(report.id, 'hide')} className="px-3 py-1.5 bg-red-100 text-red-500 border-none rounded-md text-xs font-semibold cursor-pointer hover:bg-red-200 transition-colors">{t('admin.moderation.actions.hide')}</button>
                          <button onClick={() => handleReportAction(report.id, 'dismiss')} className="px-3 py-1.5 bg-[#f5f5f7] text-[#666666] border-none rounded-md text-xs font-semibold cursor-pointer hover:bg-[#fafafa] transition-colors">{t('admin.moderation.actions.dismiss')}</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 숨김 게시글 */}
          {activeSubMenu === 'hidden-posts' && (
            <div className="animate-fade-in">
              <h2 className="text-lg font-bold text-[#1d1d1f] mb-4">{t('admin.moderation.hiddenPosts')}</h2>
              {hiddenPosts.length === 0 ? (
                <p className="text-center py-12 px-6 bg-[#f5f5f7] border border-[#e2e8f0] rounded-xl text-[#767676] text-[15px]">숨겨진 게시글이 없습니다.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {hiddenPosts.map((post) => (
                    <div key={post.id} className="bg-[#f5f5f7] border border-[#e2e8f0] rounded-lg px-5 py-4 flex justify-between items-center">
                      <div className="flex flex-col gap-1">
                        <strong className="text-[15px] text-[#1d1d1f]">{post.title}</strong>
                        <span className="text-[13px] text-[#666666]">{post.author.name}</span>
                        <span className="text-[13px] text-[#666666]">{t('admin.moderation.reportCount')}: {post.reportCount}</span>
                      </div>
                      <button onClick={() => handleRestorePost(post.id)} className="px-3 py-1.5 bg-blue-100 text-blue-500 border-none rounded-md text-xs font-semibold cursor-pointer hover:bg-blue-200 transition-colors">
                        {t('admin.moderation.actions.restore')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 프로젝트 관리 */}
          {activeSubMenu === 'project-board' && <ProjectManagement />}

          {/* 태스크 요약 위젯 (DevTeam으로 연결) */}
          {activeSubMenu === 'project-tracking' && <TaskSummaryWidget />}

          {/* API 대시보드 */}
          {activeSubMenu === 'api-dashboard' && (
            <ApiDashboard
              apiList={apiList}
              apiFilter={apiFilter}
              setApiFilter={setApiFilter}
              apiKeyStatus={apiKeyStatus}
              apiKeyMapping={apiKeyMapping}
              handleStatusClick={handleStatusClick}
              activeTooltip={activeTooltip}
              setActiveTooltip={setActiveTooltip}
            />
          )}

          {/* 서버 모니터링 */}
          {(activeSubMenu === 'server-monitor' || activeSubMenu === 'pm2-processes' ||
            activeSubMenu === 'database' || activeSubMenu === 'logs') && (
            <ServerMonitor activeSection={
              activeSubMenu === 'server-monitor' ? 'system' :
              activeSubMenu === 'pm2-processes' ? 'pm2' :
              activeSubMenu === 'database' ? 'database' :
              activeSubMenu === 'logs' ? 'logs' : 'system'
            } />
          )}
        </div>
      </main>

      {/* API 키 모달 */}
      {keyModal.open && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] animate-fade-in" onClick={() => setKeyModal({ open: false, keyName: '', keyLabel: '', keyDesc: '' })}>
          <div className="bg-white rounded-xl w-[90%] max-w-[480px] shadow-2xl animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 py-5 border-b border-[#e2e8f0]">
              <h3 className="m-0 text-lg font-bold text-[#1d1d1f]">{keyModal.keyLabel} API 키 {apiKeyStatus[keyModal.keyName] ? '변경' : '설정'}</h3>
              <button className="w-8 h-8 flex items-center justify-center bg-transparent border-none text-2xl text-[#767676] cursor-pointer rounded-md hover:bg-[#f5f5f7] hover:text-[#1d1d1f] transition-colors" onClick={() => setKeyModal({ open: false, keyName: '', keyLabel: '', keyDesc: '' })}>×</button>
            </div>
            <div className="p-6">
              <p className="m-0 mb-4 text-sm text-[#666666] leading-relaxed">{keyModal.keyDesc}</p>
              {apiKeyStatus[keyModal.keyName] && (
                <div className="mb-4 px-4 py-3 bg-green-100 rounded-lg">
                  <span className="text-green-600 text-sm">✅ 현재 키가 설정되어 있습니다</span>
                </div>
              )}
              {apiKeyInfo[keyModal.keyName]?.guide && (
                <div className="mb-5 p-4 bg-blue-50 border border-blue-200 border-l-4 border-l-blue-500 rounded-lg">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-bold text-blue-500">API 키 발급 방법</span>
                    <a href={apiKeyInfo[keyModal.keyName].guide.url} target="_blank" rel="noopener noreferrer" className="text-[13px] text-blue-500 font-semibold no-underline hover:underline">발급 페이지 바로가기 →</a>
                  </div>
                  <ul className="m-0 p-0 list-none">
                    {apiKeyInfo[keyModal.keyName].guide.steps.map((step, idx) => (
                      <li key={idx} className="text-[13px] text-slate-700 py-1 pl-3 border-l-2 border-blue-300 mb-1">{step}</li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-3 border-t border-dashed border-blue-300 text-[13px]">
                    <span className="font-bold text-blue-500 mr-2">요금:</span>
                    <span className="text-slate-600">{apiKeyInfo[keyModal.keyName].guide.pricing}</span>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-[#1d1d1f]">API 키</label>
                <input
                  type="password"
                  placeholder={apiKeyInfo[keyModal.keyName]?.placeholder || 'API 키 입력...'}
                  value={modalKeyValue}
                  onChange={(e) => setModalKeyValue(e.target.value)}
                  autoFocus
                  className="px-4 py-3 bg-[#f5f5f7] border border-[#e2e8f0] rounded-lg text-sm font-mono focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-[#f5f5f7] rounded-b-xl">
              {apiKeyStatus[keyModal.keyName] && (
                <button className="px-5 py-2.5 bg-red-100 text-red-500 border-none rounded-lg text-sm font-semibold cursor-pointer hover:bg-red-200 transition-colors mr-auto" onClick={() => deleteApiKey(keyModal.keyName)}>키 삭제</button>
              )}
              <button className="px-5 py-2.5 bg-[#f5f5f7] text-[#666666] border border-[#e2e8f0] rounded-lg text-sm font-semibold cursor-pointer hover:bg-[#fafafa] hover:border-[#cbd5e1] transition-colors" onClick={() => setKeyModal({ open: false, keyName: '', keyLabel: '', keyDesc: '' })}>취소</button>
              <button className="px-5 py-2.5 bg-blue-500 text-white border-none rounded-lg text-sm font-semibold cursor-pointer hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed" onClick={saveKeyFromModal} disabled={savingKeys}>
                {savingKeys ? '저장 중...' : apiKeyStatus[keyModal.keyName] ? '키 변경' : '키 저장'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Admin;
